import type { ChangeEventHandler, FC } from 'react'
import { useState } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import { Input } from '@jotta/ui/Input'
import { Label } from '@jotta/ui/Label'

import { Paragraph } from '../../Typography/Text/Text'
import type { RadixDialogProps } from '../RadixDialog/RadixDialog'
import { RadixDialog } from '../RadixDialog/RadixDialog'
export interface VerifyOtpDialogProps extends RadixDialogProps {
  destructive?: boolean
  /** Verify button loading state  */
  isLoading?: boolean
  /** Verification handler  */
  onVerify: (value: string) => void
}
export const VerifyOtpDialog: FC<VerifyOtpDialogProps> = ({
  title,
  smallTitle,
  destructive,
  errorMessage,
  isLoading,
  onClose,
  skipCloseButton,
  onVerify,
}) => {
  const [otp, setOtp] = useState<string>('')
  const [otpFormType, setOtpFormType] = useState<'otp' | 'backup_code'>('otp')
  const submitDisabled = otpFormType === 'otp' && otp.length !== 6
  const onSubmit = () => {
    if (!submitDisabled) {
      onVerify(otp)
    }
  }
  const handleChange: ChangeEventHandler<HTMLInputElement> = e => {
    setOtp(e.target.value)
  }

  const fieldId = otpFormType === 'otp' ? 'otp-code' : 'backup-code'
  const fieldName = otpFormType === 'otp' ? 'otp' : 'backup_code'

  return (
    <RadixDialog
      title={title || t`Authentication Required`}
      errorMessage={errorMessage}
      onClose={onClose}
      skipCloseButton={skipCloseButton}
      buttons={[
        {
          action: 'close',
          label: t`Cancel`,
          type: 'outline',
        },
        {
          label: t`Verify`,
          disabled: submitDisabled,
          loading: isLoading,
          type: destructive ? 'destructive' : 'primary',
          action: onSubmit,
        },
      ]}
    >
      <form
        onSubmit={e => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Label htmlFor={fieldId}>
          <span>
            {smallTitle ||
              (otpFormType === 'backup_code' ? (
                <Trans>
                  Enter one of your <strong>backup codes</strong>
                </Trans>
              ) : (
                <Trans>
                  Enter the six-digit code from your{' '}
                  <strong>authenticator app</strong>
                </Trans>
              ))}
          </span>
        </Label>
        {otpFormType === 'otp' && (
          <>
            <Input
              data-testid="otp-code"
              name={fieldName}
              maxLength={6}
              value={otp}
              onChange={handleChange}
              autoFocus
              placeholder="XXX-XXX"
              autoComplete="one-time-code"
              inputMode="numeric"
            />
            <Paragraph>
              <button
                type="button"
                className="btn btn-link"
                data-testid="toggle-backup"
                onClick={() => {
                  setOtpFormType('backup_code')
                  setOtp('')
                }}
              >
                <Trans>Use a backup code instead</Trans>
              </button>
            </Paragraph>
          </>
        )}
        {otpFormType === 'backup_code' && (
          <>
            <Input
              data-testid="backup-code"
              name={fieldName}
              value={otp}
              onChange={handleChange}
              autoFocus
              placeholder={t`Backup code`}
              autoComplete="off"
            />
            <Paragraph>
              <button
                type="button"
                data-testid="toggle-otp"
                className="btn btn-link"
                onClick={() => {
                  setOtpFormType('otp')
                  setOtp('')
                }}
                aria-controls="otp-code"
              >
                <Trans>Use authenticator app instead</Trans>
              </button>
            </Paragraph>
          </>
        )}
      </form>
    </RadixDialog>
  )
}
